import { Link } from 'react-router-dom'
import { APIBASE } from '../../../constants/api.constants'
import { APIROUTES, fillPath } from '../../../constants/routes.constants'
import type { BuildProductCardDto } from '../../../viewmodels/useBuildProducts'

/**
 * BuildProductCard — one tile in the /build list. Parallel to
 * ShopProductCard; links to /build/:id and pulls thumbnails from
 * /build/media/:id?thumb=1.
 */
function rupees(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

function priceLabel(min: number | null, max: number | null): string {
  if (min === null && max === null) return '—'
  if (min === max || min === null || max === null) return rupees((min ?? max) as number)
  return `${rupees(min)} – ${rupees(max)}`
}

export function BuildProductCard({ product }: { readonly product: BuildProductCardDto }): JSX.Element {
  const detailHref = `/build/${encodeURIComponent(product.id)}`
  const thumbnailUrl =
    product.thumbnail_media_id === null
      ? null
      : `${APIBASE}${fillPath(APIROUTES.BUILDMEDIA, { id: product.thumbnail_media_id })}?thumb=1`
  // /build intentionally ignores stock — it's a store-builder selection
  // surface, not a checkout. No "out of stock" treatment. Kiran 2026-06-06.
  return (
    <Link
      to={detailHref}
      // Cinematic hover lift — mirrors ShopProductCard so /shop and
      // /build feel like the same film at different intensities.
      className="group flex flex-col overflow-hidden rounded-2xl border border-border/40 bg-bg-primary/72 shadow-sm backdrop-blur-md transition-all duration-500 ease-cinematic hover:-translate-y-1 hover:shadow-xl hover:border-forest/60"
    >
      <div className="aspect-square w-full overflow-hidden bg-parchment/40">
        {thumbnailUrl !== null ? (
          <img
            src={thumbnailUrl}
            alt={product.name}
            loading="lazy"
            decoding="async"
            width={800}
            height={800}
            className="h-full w-full object-cover transition-transform duration-700 ease-cinematic group-hover:scale-[1.06]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-body text-body-sm text-text-secondary">
            No image
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="font-body text-body-xs text-text-secondary">{product.category_name}</p>
        <h3 className="font-heading text-h4 text-charcoal leading-tight line-clamp-2">{product.name}</h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          <span className="font-mono text-body text-charcoal">
            {priceLabel(product.min_price_paisa, product.max_price_paisa)}
          </span>
        </div>
      </div>
    </Link>
  )
}
